import { useState } from 'react';
import { carePlans, residentCarePlanGoalPatch, residentCarePlanReview, type ApiError, type CarePlansResponse } from '@aithora/shared';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { type ResidentsCtx, useApi } from './common';
import { plansVals, type PlansUi } from './plansVals';
import { PlansView } from './PlansView';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';

/** Care Plans (design rIsPlans, T3102): every plan in scope against the 42 CFR 483.21 clocks. */
export function PlansTab({ ctx, openChart }: { ctx: ResidentsCtx; openChart: (id: string) => void }) {
  const pick = facilitiesParam(usePickedIds());
  const q = useApi<CarePlansResponse>((c, signal) => carePlans(c, { query: pick ? { facilities: pick } : undefined, signal }), [pick]);
  const [ui, setUiState] = useState<PlansUi>({ sel: null, filter: null, goalDone: {}, signed: {} });
  const [err, setErr] = useState<ApiError | null>(null);
  const setUi = (p: Partial<PlansUi>) => setUiState((s) => ({ ...s, ...p }));
  if (q.loading && !q.data && !q.error) return <Skeleton rows={6} label="Loading care plans" />;
  if (!q.data) {
    // GET /care-plans (served): with no plans the tab says so rather than guessing.
    return q.error?.status === 404 ? (
      <EmptyState title="Care plans move here when the back end lists them" body="Each resident's plan against the baseline, comprehensive and quarterly clocks." waitingOn="GET /care-plans" />
    ) : q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : null;
  }
  const V = plansVals({
    plans: q.data.plans,
    ui,
    setUi,
    canWrite: ctx.canAct,
    openChart,
    markMet: (p, gid) => {
      setErr(null);
      residentCarePlanGoalPatch(ctx.client, { params: { id: p.residentId, gid }, body: { state: 'met' } })
        .then(() => setUi({ goalDone: { ...ui.goalDone, [p.residentId + ':' + gid]: true } }))
        .catch((e: ApiError) => setErr(e));
    },
    sign: (p) => {
      setErr(null);
      residentCarePlanReview(ctx.client, { params: { id: p.residentId }, body: {} })
        .then(() => { setUi({ signed: { ...ui.signed, [p.residentId]: true } }); q.reload(); })
        .catch((e: ApiError) => setErr(e));
    },
  });
  return (
    <>
      <PlansView V={{ ...V, rIsPlans: true }} />
      {err && <ErrorState error={err} />}
    </>
  );
}
